import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {Router} from '@angular/router';
import {FormGroup} from '@angular/forms';

@Injectable({providedIn: 'root'})
export class NotificationsService {
  deeplinks = {
    // full list: https://4csolutions.atlassian.net/wiki/spaces/AB/pages/1699184641/Deeplinks
    event: [
      {name: 'Event', link: 'beappic://event/{actor_id}'},
      {name: 'Event Info', link: 'beappic://event/{actor_id}/info'},
      {name: 'Event Line-up', link: 'beappic://event/{actor_id}/lineup'},
      {name: 'Event Timetable', link: 'beappic://event/{actor_id}/timetable'},
      {name: 'Event Visitors', link: 'beappic://event/{actor_id}/visitors'},
      {name: 'Event Visitors meetup', link: 'beappic://event/{actor_id}/meetup'},
      {name: 'Event Invite Facebook Friend', link: 'beappic://event/{actor_id}/invite-fb-friends'},
      {name: 'Event Share', link: 'beappic://event/{actor_id}/share'},
      {name: 'Event Photos', link: 'beappic://event/{actor_id}/photos'},
      {name: 'Event Reviews', link: 'beappic://event/{actor_id}/reviews'},
      {name: 'Event Map', link: 'beappic://event/{actor_id}/map'},
      {name: 'Event Tickets', link: 'beappic://event/{actor_id}/tickets'},
      {name: 'Event Chats', link: 'beappic://event/{actor_id}/chat'},
      {name: 'Ticket Wallet', link: 'beappic://mytickets'},
    ],
    page: [
      {name: 'Page / space', link: 'beappic://page/{actor_id}'},
      {name: 'Page start posting', link: 'beappic://page/{actor_id}/post'},
    ],
    wingame: [
      {name: 'Event', link: 'beappic://event/{actor_id}'},
      {name: 'Ticket Wallet', link: 'beappic://mytickets'},
    ],
    shop: [{name: 'Shop', link: 'beappic://shop'}],
  };

  labels = [
    {value: 'event', name: 'Event'},
    {value: 'page', name: 'Page'},
    {value: 'shop', name: 'Shop'},
    {value: 'voucher', name: 'Voucher'},
    {value: 'other', name: 'Other'},
  ];

  soundChoices = [
    {value: 0, name: 'default', label: 'Default Sound'},
    {value: 1, name: 'no_sound', label: 'No Sound'},
  ];

  pageTypes = [
    {id: 'event', name: 'Event'},
    {id: 'artist', name: 'Artist'},
    {id: 'venue', name: 'Venue'},
    {id: 'concept', name: 'Concept'},
    {id: 'user', name: 'User'},
    {id: 'music_style', name: 'Music Style'},
  ];

  constructor(
    private http: HttpClient,
    private router: Router,
  ) {}

  notificationsEventVerbs = () => this.http.get('/rest-api/v1/admin/notifications/verbs?actor_content_type=event');
  notificationsPageVerbs = () =>
    this.http.get('/rest-api/v1/admin/notifications/verbs?actor_content_type=communitypagemodel');
  postNotifications = body => this.http.post('/rest-api/v1/admin/target-audience/notifications', body);
  deliveryStatuses = (filters?) =>
    this.http.get('/rest-api/v1/admin/notifications/delivery_statuses', {params: filters});
  targetAudiences = id => this.http.get(`/rest-api/v1/admin/target-audiences/${id}`);
  cancelNotification = id => this.http.put(`/rest-api/v1/admin/notifications/delivery_statuses/${id}/cancel`, {});
  updateAudience = (id, audience) =>
    this.http.put(`rest-api/v1/admin/target-audiences/${id}`, {target_audience: audience});
  rescheduleNotification = (id, date) =>
    this.http.put(`rest-api/v1/admin/notifications/delivery_statuses/${id}/reschedule`, date);
  getNotificationsVerbs = (filters?) => this.http.get(`/rest-api/v1/admin/notifications/verbs`, {params: filters});
  getNotificationsLabelsAndGroups = () =>
    this.http.get(`/rest-api/v1/admin/notifications/verbs/get_verb_labels_and_groups`);
  getPageTypes = () => this.http.get(`/rest-api/v1/community/page_types`);
  getNotificationDeliveryStatus = id => this.http.get(`/rest-api/v1/admin/notifications/delivery_statuses?&id=${id}`);
  updateNotifications = body => this.http.patch('/rest-api/v1/admin/target-audience/notifications', body);
  getRequestData = id => this.http.get(`/rest-api/v1/admin/notifications/request/${id}`);

  pendingPushes = (filters?) =>
    this.http.get('/rest-api/v1/admin/notifications/delivery_statuses/scheduled_push_batches', {params: filters});
  pausePushesQueue = () =>
    this.http.put('/rest-api/v1/admin/notifications/delivery_statuses/pause_pushes_queue', {});
  resumePushesQueue = () =>
    this.http.put('/rest-api/v1/admin/notifications/delivery_statuses/resume_pushes_queue', {});
  clearPushesQueue = () =>
    this.http.put('/rest-api/v1/admin/notifications/delivery_statuses/clear_pushes_queue', {});
  movePushUp = batchId =>
    this.http.post('/rest-api/v1/admin/notifications/delivery_statuses/move_up', {batch_id: batchId});

  getSearchTags = () => this.http.get(`/rest-api/v2/search/presets`);

  removeTags(value) {
    const parser = new DOMParser();
    value = parser.parseFromString(value, 'text/html');
    value = value.documentElement.innerText;
    return value;
  }

  getNextPage(response) {
    const taskId = response['task_id'];
    if (taskId) {
      this.router.navigate(['admin', 'notifications', 'progress', taskId]);
    } else {
      this.router.navigate(['admin', 'notifications', 'delivery-statuses']);
    }
  }

  getMessageLength(field) {
    if (!field.value) {
      return 0;
    }
    const eventName = document.getElementById('eventName') as HTMLInputElement;
    const pageName = document.getElementById('pageName') as HTMLInputElement;
    const fieldContent = field.value;

    let actorLength = 0;
    if (fieldContent.includes('{actor}')) {
      if (eventName) {
        const regex = /(.*)\s\(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}\)/;
        const match = regex.exec(eventName.value);
        const output = match ? match[1] : eventName.value;
        actorLength = output.length - 7; // -7 because string contains '{actor}'
      } else if (pageName) {
        actorLength = pageName.value.length - 7; // -7 because string contains '{actor}'
      }
    }

    if (field.value) {
      return this.removeTags(field.value).length + actorLength;
    }
    return 0;
  }

  getFormErrors(form) {
    const errors = [];
    Object.keys(form.controls).forEach(key => {
      const control = form.controls[key];
      // for FormControls
      if (control.errors) {
        for (const e in control.errors) {
          const error = `${key}: ${JSON.stringify(form.controls[key].errors[key])}`;
          errors.push(error);
        }
      }
      // FormGroups - check its children one level deeper
      if (control instanceof FormGroup) {
        Object.keys(control.controls).forEach(childKey => {
          if (control.controls[childKey].errors) {
            for (const e in control.controls[childKey].errors) {
              const error = `${key}.${childKey}: ${JSON.stringify(control.controls[childKey].errors[key])}`;
              errors.push(error);
            }
          }
        });
      }
    });

    if (form.errors) {
      Object.keys(form.errors).forEach(error => {
        errors.push(error);
      });
    }
    return errors;
  }
}
